import OverlayMenuButton, { IMenuItem } from '@nkr/components/overlay-menu-button';
import { XmStatus, XmType } from '@nkr/enums/index';
import { IXmTracking } from '@nkr/models/index';
import { XmListType } from '@nkr/store/index';
import { CellFocusedEvent, ColDef, FirstDataRenderedEvent, GridReadyEvent, GridSizeChangedEvent, ICellRendererParams, IHeaderParams, SuppressKeyboardEventParams, ValueGetterParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useMemo, useState } from 'react';
import SortableHeaderComponent from '../admin/center/list/common/sortable-header';
import { Actions } from './actions';
import { GridWrapper } from './styles';
import UpdateDeadline from './update-deadline';
import { Button } from 'react-bootstrap';
import { Utility } from '@nkr/services/utility';

const KEY_UP = 'ArrowUp';
const KEY_DOWN = 'ArrowDown';
const KEY_LEFT = 'ArrowLeft';
const KEY_RIGHT = 'ArrowRight';

interface IDeadlineRendererProps extends ICellRendererParams { onUpdate: (id: number, value: string) => void };

interface IProps {
    data: IXmTracking[],
    centerId: number,
    listType: XmListType,
    onAction: (action: number, id: number) => void,
    onXmTrackingDetails: (id: number) => void,
    onUpdateDeadline: (id: number, deadline: string) => void,
    onShowUploadFinalXm: (donorId: number, recipientId: number) => void
}

const Grid = (props: IProps) => {
    const [menuDisplayed, setMenuDisplayed] = useState(false);

    const onGridReady = (e: GridReadyEvent) => {
        e.api.sizeColumnsToFit()
    }

    const onDataFirstRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    }

    const onGridSizeChanged = (e: GridSizeChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const handleMenuToggle = (show: boolean) => {
        setMenuDisplayed(show);
    }

    const renderRequestedFromActions = (xm: IXmTracking) => {
        let cancelOnly = [XmStatus.ShippedOut, XmStatus.Received, XmStatus.ShippedRemote, XmStatus.XmRequested, XmStatus.ShippedBack, XmStatus.ShippedRemote].indexOf(xm.xmStatusId) < 0;
        let showMarkReceived = xm.xmTypeId === XmType.ExploratoryXm
            ? xm.xmStatusId === XmStatus.XmRequested
            : [XmStatus.ShippedOut].indexOf(xm.xmStatusId) >= 0;
        let showShipToRemoteDonor = xm.xmStatusId == XmStatus.Received;
        let showDonorBloodDrawnShipped = [XmStatus.Received, XmStatus.ShippedRemote].indexOf(xm.xmStatusId) >= 0;
        let showRequestUpdate = [XmStatus.XmRequested, XmStatus.ShippedBack, XmStatus.Received, XmStatus.ShippedRemote].indexOf(xm.xmStatusId) >= 0;
        let showTrackPackage = [XmStatus.ShippedOut, XmStatus.ShippedBack, XmStatus.ShippedRemote].indexOf(xm.xmStatusId) >= 0;

        let menu: IMenuItem[] = [
            {
                id: xm.id,
                action: Actions.MarkReceived,
                include: showMarkReceived,
                selectable: true,
                element: <>Mark Received</>
            },
            {
                id: xm.id,
                action: Actions.MarkRemoteShipped,
                include: showShipToRemoteDonor,
                selectable: true,
                element: <>Ship to Remote Donor</>
            },
            {
                id: xm.id,
                action: Actions.DonorBloodDrawnShipped,
                include: showDonorBloodDrawnShipped,
                selectable: true,
                element: <>Donor Blood Drawn and Shipped</>
            },
            {
                id: xm.id,
                action: Actions.RequestUpdate,
                include: showRequestUpdate,
                selectable: true,
                element: <>Request Update</>
            },
            {
                id: xm.id,
                action: Actions.TrackPackage,
                include: showTrackPackage,
                selectable: true,
                element: <>Track Package</>
            },
            {
                id: xm.id,
                action: -1,
                include: !cancelOnly,
                selectable: false,
                element: <div style={{ height: 10 }}></div>
            },
            {
                id: xm.id,
                action: Actions.CancelRequest,
                include: true,
                selectable: true,
                element: <>Cancel Request</>
            }
        ];

        return (
            <OverlayMenuButton
                menu={menu}
                bsStyle='primary'
                bsSize='xs'
                title='Actions'
                includeCaret={true}
                listItemStyle={{ padding: '3px 10px', fontSize: 14 }}
                onToggle={handleMenuToggle}
                onSelect={(id, action) => props.onAction(action, id)}
            />
        );
    }

    const renderRequestedByActions = (xm: IXmTracking) => {
        let cancelOnly = [XmStatus.ShippedOut, XmStatus.Received, XmStatus.ShippedRemote, XmStatus.XmRequested, XmStatus.ShippedBack, XmStatus.ShippedRemote, XmStatus.ReceivedBack].indexOf(xm.xmStatusId) < 0;
        let showRecordResults = xm.xmStatusId != XmStatus.XmCancelled && xm.xmStatusId != XmStatus.PendingNkrLab;
        let showDonorHlaFile = xm.xmStatusId == XmStatus.ReceivedBack && xm.donorRawTypingUploadPath != '';
        let showDonorCtReport = xm.xmStatusId == XmStatus.ReceivedBack && xm.donorCtReportUploadPath != '';
        let showDonorMedicalChart = xm.xmStatusId == XmStatus.ReceivedBack && xm.donorMedChartUploadPath != '';
        let showMarkReceived = xm.xmTypeId === XmType.ExploratoryXm
            ? false
            : [XmStatus.XmRequested, XmStatus.Received, XmStatus.ShippedBack, XmStatus.ShippedRemote].indexOf(xm.xmStatusId) >= 0;
        let showMarkReceivedBack = xm.xmTypeId === XmType.ExploratoryXm
            ? [XmStatus.ShippedBack, XmStatus.ShippedRemote].indexOf(xm.xmStatusId) >= 0
            : false;
        let showRequestUpdate = [XmStatus.XmRequested, XmStatus.ShippedBack, XmStatus.Received, XmStatus.ShippedRemote].indexOf(xm.xmStatusId) >= 0;
        let showTrackPackage = [XmStatus.ShippedOut, XmStatus.ShippedBack, XmStatus.ShippedRemote].indexOf(xm.xmStatusId) >= 0;

        let menu: IMenuItem[] = [
            {
                id: xm.id,
                action: Actions.RecordResults,
                include: showRecordResults,
                selectable: true,
                element: <>Record Results</>
            },
            {
                id: xm.id,
                action: Actions.DonorHlaFile,
                include: showDonorHlaFile,
                selectable: true,
                element: <>Donor HLA File</>
            },
            {
                id: xm.id,
                action: Actions.DonorCtReport,
                include: showDonorCtReport,
                selectable: true,
                element: <>Donor CT Report</>
            },
            {
                id: xm.id,
                action: Actions.DonorMedicalChart,
                include: showDonorMedicalChart,
                selectable: true,
                element: <>Donor Medical Chart</>
            },
            {
                id: xm.id,
                action: Actions.MarkReceived,
                include: showMarkReceived,
                selectable: true,
                element: <>Mark Received</>
            },
            {
                id: xm.id,
                action: Actions.MarkReceivedBack,
                include: showMarkReceivedBack,
                selectable: true,
                element: <>Mark Received</>
            },
            {
                id: xm.id,
                action: Actions.RequestUpdate,
                include: showRequestUpdate,
                selectable: true,
                element: <>Request Update</>
            },
            {
                id: xm.id,
                action: Actions.TrackPackage,
                include: showTrackPackage,
                selectable: true,
                element: <>Track Package</>
            },
            {
                id: xm.id,
                action: -1,
                include: xm.uploadFinalXmResults,
                selectable: false,
                element: <div style={{ height: 10 }}></div>
            },
            {
                id: xm.id,
                action: Actions.UploadFinalXm,
                include: xm.uploadFinalXmResults,
                selectable: true,
                element: <>Upload Final XM Results</>
            },
            {
                id: xm.id,
                action: -1,
                include: !cancelOnly,
                selectable: false,
                element: <div style={{ height: 10 }}></div>
            },
            {
                id: xm.id,
                action: Actions.CancelRequest,
                include: true,
                selectable: true,
                element: <>Cancel Request</>
            }
        ];

        return (
            <OverlayMenuButton
                menu={menu}
                bsStyle='primary'
                bsSize='xs'
                title='Actions'
                includeCaret={true}
                listItemStyle={{ padding: '3px 10px', fontSize: 14 }}
                onToggle={handleMenuToggle}
                onSelect={(id, action) => props.onAction(action, id)}
            />
        );
    };

    const renderPendingShipping = (xm: IXmTracking) => {
        let menu: IMenuItem[] = [
            {
                id: xm.id,
                action: Actions.MarkShippedOut,
                include: true,
                selectable: true,
                element: <>Mark Shipped</>
            },
            {
                id: xm.id,
                action: -1,
                include: true,
                selectable: false,
                element: <div style={{ height: 10 }}></div>
            },
            {
                id: xm.id,
                action: Actions.CancelRequest,
                include: true,
                selectable: true,
                element: <>Cancel Request</>
            }
        ];

        return (
            <OverlayMenuButton
                menu={menu}
                bsStyle='primary'
                bsSize='xs'
                title='Actions'
                includeCaret={true}
                listItemStyle={{ padding: '3px 10px', fontSize: 14 }}
                onToggle={handleMenuToggle}
                onSelect={(id, action) => props.onAction(action, id)}
            />
        );
    };

    const renderShippedPendingDonorCenter = (xm: IXmTracking) => {
        let cancelOnly = [XmStatus.ShippedOut, XmStatus.ShippedBack, XmStatus.ShippedRemote].indexOf(xm.xmStatusId) < 0 && xm.xmStatusId != XmStatus.PendingNkrLab;
        let showTrackPackage = [XmStatus.ShippedOut, XmStatus.ShippedBack, XmStatus.ShippedRemote].indexOf(xm.xmStatusId) >= 0;
        let showMarkReceived = [XmStatus.ShippedOut].indexOf(xm.xmStatusId) >= 0;
        let showMarkReceivedBack = [XmStatus.ShippedBack].indexOf(xm.xmStatusId) >= 0;
        let showCryoSamplesShipped = xm.xmStatusId == XmStatus.PendingNkrLab;

        let menu: IMenuItem[] = [
            {
                id: xm.id,
                action: Actions.TrackPackage,
                include: showTrackPackage,
                selectable: true,
                element: <>Track Package</>
            },
            {
                id: xm.id,
                action: Actions.MarkReceived,
                include: showMarkReceived,
                selectable: true,
                element: <>Mark Received</>
            },
            {
                id: xm.id,
                action: Actions.MarkReceivedBack,
                include: showMarkReceivedBack,
                selectable: true,
                element: <>Mark Received Back</>
            },
            {
                id: xm.id,
                action: Actions.MarkShippedBack,
                include: showCryoSamplesShipped,
                selectable: true,
                element: <>Cryo Samples Shipped</>
            },
            {
                id: xm.id,
                action: -1,
                include: !cancelOnly,
                selectable: false,
                element: <div style={{ height: 10 }}></div>
            },
            {
                id: xm.id,
                action: Actions.CancelRequest,
                include: true,
                selectable: true,
                element: <>Cancel Request</>
            }
        ];

        return (
            <OverlayMenuButton
                menu={menu}
                bsStyle='primary'
                bsSize='xs'
                title='Actions'
                includeCaret={true}
                listItemStyle={{ padding: '3px 10px', fontSize: 14 }}
                onToggle={handleMenuToggle}
                onSelect={(id, action) => props.onAction(action, id)}
            />
        );
    };

    const renderShippedPendingRecipientCenter = (xm: IXmTracking) => {
        let cancelOnly = [XmStatus.ShippedOut, XmStatus.ShippedBack, XmStatus.ShippedRemote].indexOf(xm.xmStatusId) < 0 && xm.xmStatusId != XmStatus.PendingNkrLab;
        let showTrackPackage = [XmStatus.ShippedOut, XmStatus.ShippedBack, XmStatus.ShippedRemote].indexOf(xm.xmStatusId) >= 0;
        let showMarkReceived = [XmStatus.ShippedOut].indexOf(xm.xmStatusId) >= 0;
        let showMarkReceivedBack = [XmStatus.ShippedBack].indexOf(xm.xmStatusId) >= 0;

        let menu: IMenuItem[] = [
            {
                id: xm.id,
                action: Actions.TrackPackage,
                include: showTrackPackage,
                selectable: true,
                element: <>Track Package</>
            },
            {
                id: xm.id,
                action: Actions.MarkReceived,
                include: showMarkReceived,
                selectable: true,
                element: <>Mark Received</>
            },
            {
                id: xm.id,
                action: Actions.MarkReceivedBack,
                include: showMarkReceivedBack,
                selectable: true,
                element: <>Mark Received Back</>
            },
            {
                id: xm.id,
                action: -1,
                include: !cancelOnly,
                selectable: false,
                element: <div style={{ height: 10 }}></div>
            },
            {
                id: xm.id,
                action: Actions.CancelRequest,
                include: true,
                selectable: true,
                element: <>Cancel Request</>
            }
        ];

        return (
            // <DropdownButton bsStyle='primary' bsSize='xs' id={'xm-action-' + xm.id} title='Actions' pullRight onSelect={(eventKey) => this.handleAction(eventKey, xm.id)}>
            //     {showTrackPackage && <MenuItem eventKey={Actions.TrackPackage}>Track Package</MenuItem>}
            //     {showMarkReceived && <MenuItem eventKey={Actions.MarkReceived}>Mark Received</MenuItem>}
            //     {showMarkReceivedBack && <MenuItem eventKey={Actions.MarkReceivedBack}>Mark Received Back</MenuItem>}

            //     {!cancelOnly && <MenuItem divider />}

            //     <MenuItem className='xm-cancel-request' eventKey={Actions.CancelRequest}>Cancel Request</MenuItem>
            // </DropdownButton>

            <OverlayMenuButton
                menu={menu}
                bsStyle='primary'
                bsSize='xs'
                title='Actions'
                includeCaret={true}
                listItemStyle={{ padding: '3px 10px', fontSize: 14 }}
                onToggle={handleMenuToggle}
                onSelect={(id, action) => props.onAction(action, id)}
            />
        );
    };

    const getColumns = () => {
        if (props.centerId > 0) {
            let columns: ColDef[] = [
                {
                    colId: 'id',
                    field: 'id',
                    headerName: 'Order #',
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Order #</SortableHeaderComponent>,
                    cellClass: 'list-cell list-cell-order',
                    width: 75,
                    hide: props.listType == XmListType.MissingFxm,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        return (<a target={'_blank'} onClick={() => props.onXmTrackingDetails(params.data.id)}>{params.data.id}</a>)
                    }
                },
                {
                    colId: 'xmTypeDescription',
                    field: 'xmTypeDescription',
                    headerName: 'Type',
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Type</SortableHeaderComponent>,
                    width: 75
                },
                {
                    colId: 'swapNumber',
                    headerName: 'Swap',
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Swap</SortableHeaderComponent>,
                    width: 75,
                    valueGetter: (params: ValueGetterParams) => {
                        let data = params.data as IXmTracking;

                        if (data.chainNumber != null && data.clusterNumber != null && data.positionNumber != null)
                            return `${data.chainNumber}/${data.clusterNumber}/${data.positionNumber}`;
                        else
                            return '';
                    },
                    comparator: (valueA: string, valueB: string) => {
                        if (!valueA && !valueB) return 0;
                        if (!valueA) return 1;
                        if (!valueB) return -1;

                        const [chainA, clusterA, positionA] = valueA.split('/').map(Number);
                        const [chainB, clusterB, positionB] = valueB.split('/').map(Number);

                        if (chainA !== chainB) return chainA - chainB;
                        if (clusterA !== clusterB) return clusterA - clusterB;
                        return positionA - positionB;
                    }
                },
                {
                    colId: 'recipientCenterAlias',
                    field: 'recipientCenterAlias',
                    headerName: 'Recipient Center',
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Recipient<br />Center</SortableHeaderComponent>,
                    width: 120
                },
                {
                    colId: 'recipientAlias',
                    field: 'recipientAlias',
                    headerName: 'Recipient Alias',
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Recipient<br />Alias</SortableHeaderComponent>,
                    width: 120
                },
                {
                    colId: 'donorCenterAlias',
                    field: 'donorCenterAlias',
                    headerName: 'Donor Center',
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Donor<br />Center</SortableHeaderComponent>,
                    width: 160,
                    cellRendererFramework: (props: ICellRendererParams) => {
                        let data = props.data as IXmTracking;

                        if (data.rdnCenterId > 0) {
                            return <>{data.donorCenterAlias}(<span style={{ color: 'red' }}>{data.rdnCenterAlias}</span>)</>;
                        }

                        return <>{data.donorCenterAlias}</>;
                    }
                },
                {
                    colId: 'donorAlias',
                    field: 'donorAlias',
                    headerName: 'Donor Alias',
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Donor Alias</SortableHeaderComponent>,
                    width: 120
                },
                {
                    colId: 'xmStatusDescription',
                    field: 'xmStatusDescription',
                    headerName: 'Status',
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Status</SortableHeaderComponent>,
                },
                {
                    colId: 'requestedDate',
                    field: 'requestedDate',
                    headerName: 'Date Requested',
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Date<br />Requested</SortableHeaderComponent>,
                    width: 105,
                    comparator: Utility.dateComparator,
                    hide: props.listType == XmListType.MissingFxm
                },
                {
                    colId: 'xmStatusDeadline',
                    field: 'xmStatusDeadline',
                    headerName: 'Deadline',
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Deadline</SortableHeaderComponent>,
                    width: 155,
                    suppressSizeToFit: true,
                    editable: false,
                    suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => {
                        const key = params.event.key;
                        const gridShouldDoNothing = key === KEY_UP || key === KEY_DOWN || key == KEY_LEFT || key == KEY_RIGHT;
                        return gridShouldDoNothing;
                    },
                    cellRendererParams: { onUpdate: props.onUpdateDeadline },
                    cellRendererFramework: (params: IDeadlineRendererProps) => {
                        let data = params.data as IXmTracking;
                        let allowUpdate = [XmStatus.XmCancelled, XmStatus.ResultsRecorded, XmStatus.FinalXmMissing].indexOf(data.xmStatusId) < 0;

                        if (allowUpdate) {
                            return (
                                <UpdateDeadline
                                    startingValue={data.xmStatusDeadline}
                                    onUpdate={(value: string) => params.onUpdate(data.id, value)}
                                />
                            );
                        } else {
                            return <>{data.xmStatusDeadline}</>
                        }
                    }
                },
                {
                    colId: 'actionMenu',
                    field: 'id',
                    headerName: '',
                    cellClass: 'action-menu',
                    hide: props.listType == XmListType.History,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IXmTracking;

                        if (props.listType == XmListType.RequestedBy)
                            return (
                                <div style={{ width: '100%', textAlign: 'center' }}>
                                    <>{renderRequestedByActions(params.data)}</>
                                </div>
                            );

                        if (props.listType == XmListType.RequestedFrom)
                            return (
                                <div style={{ width: '100%', textAlign: 'center' }}>
                                    <>{renderRequestedFromActions(params.data)}</>
                                </div>
                            );

                        if (props.listType == XmListType.All && data.xmStatusId != XmStatus.XmCancelled && data.xmStatusId != XmStatus.ResultsRecorded) {
                            if (data.isRequestingCenter)
                                return (
                                    <div style={{ width: '100%', textAlign: 'center' }}>
                                        <>{renderRequestedByActions(params.data)}</>
                                    </div>
                                );
                            else
                                return (
                                    <div style={{ width: '100%', textAlign: 'center' }}>
                                        <>{renderRequestedFromActions(params.data)}</>
                                    </div>
                                );
                        }

                        if (props.listType == XmListType.MissingFxm) {
                            return (
                                <Button bsStyle='primary' bsSize='xs' onClick={() => props.onShowUploadFinalXm(data.donorId, data.recipientId)}>Upload Final XM</Button>
                            )
                        }

                        return null;
                    },
                    width: 85
                }
            ];

            return columns;
        } else {
            let columns: ColDef[] = [];

            if (props.listType != XmListType.AdminCompleted && props.listType != XmListType.AdminCancelled) {
                columns = [
                    {
                        colId: 'id',
                        field: 'id',
                        headerName: 'Order #',
                        headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Order #</SortableHeaderComponent>,
                        cellClass: 'list-cell list-cell-order',
                        width: 75,
                        cellRendererFramework: (params: ICellRendererParams) => {
                            return (<a target={'_blank'} onClick={() => props.onXmTrackingDetails(params.data.id)}>{params.data.id}</a>)
                        }
                    },
                    {
                        colId: 'xmTypeDescription',
                        field: 'xmTypeDescription',
                        headerName: 'Type',
                        headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Type</SortableHeaderComponent>,
                        width: 75
                    },
                    {
                        colId: 'recipientCenterAlias',
                        field: 'recipientCenterAlias',
                        headerName: 'Recipient Center',
                        headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Recipient Center</SortableHeaderComponent>,
                        width: 140
                    },
                    {
                        colId: 'recipientAlias',
                        field: 'recipientAlias',
                        headerName: 'Recipient Alias',
                        headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Recipient Alias</SortableHeaderComponent>,
                        width: 140
                    },
                    {
                        colId: 'donorCenterAlias',
                        field: 'donorCenterAlias',
                        headerName: 'Donor Center',
                        headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Donor Center</SortableHeaderComponent>,
                        width: 160,
                        cellRendererFramework: (props: ICellRendererParams) => {
                            let data = props.data as IXmTracking;

                            if (data.rdnCenterId > 0) {
                                return <>{data.donorCenterAlias} (<span style={{ color: 'red' }}>{data.rdnCenterAlias}</span>)</>;
                            }

                            return <>{data.donorCenterAlias}</>;
                        }
                    },
                    {
                        colId: 'donorAlias',
                        field: 'donorAlias',
                        headerName: 'Donor Alias',
                        headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Donor Alias</SortableHeaderComponent>,
                        width: 120
                    },
                    {
                        colId: 'bloodType',
                        headerName: 'Blood Type',
                        headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Blood Type</SortableHeaderComponent>,
                        valueGetter: (props: ValueGetterParams) => {
                            let data = props.data as IXmTracking;
                            return (data.isCryoXm || data.isViracorCryo) ? 'Cryo' : 'Fresh'
                        },
                        width: 85
                    },
                    {
                        colId: 'swapNumber',
                        headerName: 'Swap',
                        headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Swap</SortableHeaderComponent>,
                        valueGetter: (params: ValueGetterParams) => {
                            let data = params.data as IXmTracking;
                            if (data.chainNumber != null && data.clusterNumber != null && data.positionNumber != null)
                                return `${data.chainNumber}/${data.clusterNumber}/${data.positionNumber}`;
                            else
                                return '';
                        },
                        cellRendererFramework: (props: ICellRendererParams) => {
                            let data = props.data as IXmTracking;

                            if (data.chainNumber != null && data.clusterNumber != null && data.positionNumber != null)
                                return <>{data.chainNumber}/{data.clusterNumber}/{data.positionNumber}</>
                            else
                                return <>--</>
                        },
                        comparator: (valueA: string, valueB: string) => {
                            if (!valueA && !valueB) return 0;
                            if (!valueA) return 1;
                            if (!valueB) return -1;

                            const [chainA, clusterA, positionA] = valueA.split('/').map(Number);
                            const [chainB, clusterB, positionB] = valueB.split('/').map(Number);

                            if (chainA !== chainB) return chainA - chainB;
                            if (clusterA !== clusterB) return clusterA - clusterB;
                            return positionA - positionB;
                        },
                        width: 55
                    },
                    {
                        colId: 'requestedDate',
                        field: 'requestedDate',
                        headerName: 'Date Requested',
                        headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Date<br />Requested</SortableHeaderComponent>,
                        comparator: Utility.dateComparator,
                        width: 105
                    },
                    {
                        colId: 'xmStatusDeadline',
                        field: 'xmStatusDeadline',
                        headerName: 'Deadline',
                        headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Deadline</SortableHeaderComponent>,
                        width: 155,
                        suppressSizeToFit: true,
                        suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => {
                            const key = params.event.key;
                            const gridShouldDoNothing = key === KEY_UP || key === KEY_DOWN || key == KEY_LEFT || key == KEY_RIGHT;
                            return gridShouldDoNothing;
                        },
                        cellRendererParams: { onUpdate: props.onUpdateDeadline },
                        cellRendererFramework: (params: IDeadlineRendererProps) => {
                            let data = params.data as IXmTracking;

                            return (
                                <UpdateDeadline
                                    startingValue={data.xmStatusDeadline}
                                    onUpdate={(value: string) => params.onUpdate(data.id, value)}
                                />
                            )
                        }
                    },
                    {
                        colId: 'xmStatusDescription',
                        field: 'xmStatusDescription',
                        headerName: 'Status',
                        headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Status</SortableHeaderComponent>,
                        width: 145
                    },
                    {
                        colId: 'actionMenu',
                        field: 'id',
                        headerName: '',
                        cellClass: 'action-menu',
                        cellRendererFramework: (params: ICellRendererParams) =>
                            <div style={{ width: '100%', textAlign: 'center' }}>
                                {props.listType == XmListType.AdminPendingShipping && (
                                    <>{renderPendingShipping(params.data)}</>
                                )}

                                {props.listType == XmListType.AdminShippedPendingDCenter && (
                                    <>{renderShippedPendingDonorCenter(params.data)}</>
                                )}

                                {props.listType == XmListType.AdminShippedPendingRCenter && (
                                    <>{renderShippedPendingRecipientCenter(params.data)}</>
                                )}
                            </div>,
                        width: 85
                    }
                ];
            } else {
                columns = [
                    {
                        colId: 'id',
                        field: 'id',
                        headerName: 'Order #',
                        headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Order #</SortableHeaderComponent>,
                        cellClass: 'list-cell list-cell-order',
                        width: 75,
                        cellRendererFramework: (params: ICellRendererParams) => {
                            return (<a target={'_blank'} onClick={() => props.onXmTrackingDetails(params.data.id)}>{params.data.id}</a>)
                        }
                    },
                    {
                        colId: 'xmTypeDescription',
                        field: 'xmTypeDescription',
                        headerName: 'Type',
                        headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Type</SortableHeaderComponent>,
                        width: 75
                    },
                    {
                        colId: 'recipientCenterAlias',
                        field: 'recipientCenterAlias',
                        headerName: 'Recipient Center',
                        headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Recipient Center</SortableHeaderComponent>,
                        width: 135
                    },
                    {
                        colId: 'recipientAlias',
                        field: 'recipientAlias',
                        headerName: 'Recipient Alias',
                        headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Recipient Alias</SortableHeaderComponent>,
                        width: 95
                    },
                    {
                        colId: 'donorCenterAlias',
                        field: 'donorCenterAlias',
                        headerName: 'Donor Center',
                        headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Donor Center</SortableHeaderComponent>,
                        cellRendererFramework: (props: ICellRendererParams) => {
                            let data = props.data as IXmTracking;

                            if (data.rdnCenterId > 0) {
                                return <>{data.donorCenterAlias} (<span style={{ color: 'red' }}>{data.rdnCenterAlias}</span>)</>;
                            }

                            return <>{data.donorCenterAlias}</>;
                        }
                    },
                    {
                        colId: 'donorAlias',
                        field: 'donorAlias',
                        headerName: 'Donor Alias',
                        headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Donor Alias</SortableHeaderComponent>,
                        width: 95
                    },
                    {
                        colId: 'swapNumber',
                        headerName: 'Swap',
                        headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Swap</SortableHeaderComponent>,
                        valueGetter: (params: ValueGetterParams) => {
                            let data = params.data as IXmTracking;
                            if (data.chainNumber != null && data.clusterNumber != null && data.positionNumber != null)
                                return `${data.chainNumber}/${data.clusterNumber}/${data.positionNumber}`;
                            else
                                return '';
                        },
                        cellRendererFramework: (props: ICellRendererParams) => {
                            let data = props.data as IXmTracking;

                            if (data.chainNumber != null && data.clusterNumber != null && data.positionNumber != null)
                                return <>{data.chainNumber}/{data.clusterNumber}/{data.positionNumber}</>
                            else
                                return <>--</>
                        },
                        comparator: (valueA: string, valueB: string) => {
                            if (!valueA && !valueB) return 0;
                            if (!valueA) return 1;
                            if (!valueB) return -1;

                            const [chainA, clusterA, positionA] = valueA.split('/').map(Number);
                            const [chainB, clusterB, positionB] = valueB.split('/').map(Number);

                            if (chainA !== chainB) return chainA - chainB;
                            if (clusterA !== clusterB) return clusterA - clusterB;
                            return positionA - positionB;
                        },
                        width: 55
                    },
                    {
                        colId: 'requestedDate',
                        field: 'requestedDate',
                        headerName: 'Date Requested',
                        headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Date<br />Requested</SortableHeaderComponent>,
                        comparator: Utility.dateComparator,
                        width: 95
                    },
                    {
                        colId: 'xmStatusDescription',
                        field: 'xmStatusDescription',
                        headerName: 'Status',
                        headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Status</SortableHeaderComponent>,
                        width: 155
                    }
                ];
            }

            return columns;
        }
    };

    const columnDefs = useMemo(() => getColumns(), [props.listType]);
    const rowData = useMemo(() => props.data, [props.data]);

    return (
        <GridWrapper className='ag-theme-bootstrap' style={{ height: props.centerId > 0 ? '60vh' : '82vh' }}>
            <AgGridReact
                defaultColDef={{
                    headerClass: 'list-header',
                    cellClass: 'list-cell',
                    sortable: true
                }}
                columnDefs={columnDefs}
                rowData={rowData}
                headerHeight={45}
                rowHeight={32}
                noRowsOverlayComponentFramework={() => <div>No XM requests were found...</div>}
                suppressRowClickSelection={true}
                suppressCellSelection={true}
                immutableData={true}
                enableCellTextSelection={true}
                rowBuffer={1000}
                getRowNodeId={(data: IXmTracking) => data.id.toString()}
                onGridReady={onGridReady}
                onFirstDataRendered={onDataFirstRendered}
                onCellFocused={(e: CellFocusedEvent) => {
                    let colId = e.column.getColId();

                    if (e.column && colId == 'actionMenu') {
                        (e.api as any).gridOptionsWrapper.gridOptions.enableCellTextSelection = false;
                    } else {
                        (e.api as any).gridOptionsWrapper.gridOptions.enableCellTextSelection = true;
                    }
                }}
                onBodyScroll={() => {
                    if (menuDisplayed) {
                        document.body.click();
                        setMenuDisplayed(false);
                    }
                }}
                onGridSizeChanged={onGridSizeChanged}
            />
        </GridWrapper>
    );
}

export default Grid;